import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { Modal } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import type { Music24PlanTier } from 'fym-shared/src/music24/music24Plans';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../components/primitives/SMLButton';
import { cleanSizeOf } from '../files/watermarkedFile';
import { WatermarkThumbTile } from '../files/WatermarkThumbTile';
import { UPSELL_MODAL_Z_INDEX } from '../upsellModalLayer';
import { CHOICE_COPY } from './choiceCopy';
import { watermarkChoiceMessages as messages } from './watermarkChoiceMessages';
import type { WatermarkChoiceRequest } from './watermarkChoiceStore';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the upsell; each part uses its own slice of it */
import styles from '../WatermarkUpsell.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface WatermarkChoiceModalProps {
  /** Null: the modal is closed. */
  request: WatermarkChoiceRequest | null;
  plan: Music24PlanTier;
  /** A member gets the owner note in the place of the first row: no price, no button. */
  isMember: boolean;
  /** "$19", or null in the native web view (no web price there). */
  price: string | null;
  /** The watermarked files of the workspace; the fine line names them. */
  files: number;
  /** False: no line promises clean copies of the old files (spec §14.1). */
  cleanCopiesAvailable: boolean;
  onRemove: () => void;
  /** The second row: always one click. */
  onContinue: () => void;
  onDismiss: () => void;
}

/**
 * The choice on each Download, share link, "Post it" and kit zip of a watermarked file (design:
 * trial-upsell a2). Row 1 opens the offer; row 2 goes on with the watermark with one click. For a
 * download, row 2 is a real `<a href download>`: the same download as the plain link.
 */
const WatermarkChoiceModalComponent: React.FC<WatermarkChoiceModalProps> = (props) => {
  const { request, plan, isMember, price, files, cleanCopiesAvailable, onRemove, onContinue, onDismiss } = props;
  const intl = useIntl();
  const label = request?.label ?? null;
  const songTitle = request?.songTitle ?? null;
  const subtitle = useMemo(
    () => [label, songTitle].filter((part) => part !== null && part !== '').join(' · '),
    [label, songTitle]
  );
  if (request === null) {
    return null;
  }
  const copy = CHOICE_COPY[request.surface];
  const isZip = request.surface === 'zip';
  const clean = isZip ? null : cleanSizeOf(request.width, request.height, request.preview);
  let cleanDetail = intl.formatMessage(messages.cleanDetailNoPrice);
  if (!cleanCopiesAvailable) {
    // The licence had its one run (spec §14.1): the first row promises only new files.
    cleanDetail =
      price === null
        ? intl.formatMessage(messages.newFilesDetailNoPrice)
        : intl.formatMessage(messages.newFilesDetail, { price });
  } else if (price !== null) {
    cleanDetail =
      clean === null
        ? intl.formatMessage(messages.cleanDetailNoSize, { price })
        : intl.formatMessage(messages.cleanDetail, { width: clean.width, height: clean.height, price });
  }
  const keepDetail =
    request.preview && !isZip && request.width !== null && request.height !== null
      ? intl.formatMessage(messages.keepDetailPreview, { width: request.width, height: request.height })
      : intl.formatMessage(messages.keepDetail);
  const keepAction = intl.formatMessage(copy.keepAction);

  return (
    <Modal
      isOpen
      toggle={onDismiss}
      centered
      zIndex={UPSELL_MODAL_Z_INDEX}
      className={styles.dialog}
      contentClassName={styles.content}
      data-testid="watermark-choice"
    >
      <div className={styles.modal}>
        <button
          type="button"
          className={styles.close}
          onClick={onDismiss}
          aria-label={intl.formatMessage(messages.close)}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
        <div className={styles.choiceHead}>
          {!isZip && <WatermarkThumbTile url={request.thumbUrl} shape={request.shape} marked />}
          <div>
            <h3 className={clsx(styles.h, styles.hSmall)}>
              {withBrandWord(intl.formatMessage(isZip ? messages.titleZip : messages.title))}
            </h3>
            {subtitle !== '' && <p className={styles.p}>{subtitle}</p>}
          </div>
        </div>
        <div className={styles.opts}>
          {isMember ? (
            <div className={styles.owner} data-testid="watermark-choice-owner-note">
              <b>{intl.formatMessage(messages.ownerNote)}</b>
              <small>
                {intl.formatMessage(cleanCopiesAvailable ? messages.ownerNoteDetail : messages.ownerNoteDetailNew)}
              </small>
            </div>
          ) : (
            <div className={clsx(styles.opt, styles.optPick)}>
              <b>{intl.formatMessage(cleanCopiesAvailable ? copy.clean : messages.newFiles)}</b>
              <small>{cleanDetail}</small>
              <SMLButton variant="primary" className={styles.optAct} onClick={onRemove}>
                {intl.formatMessage(messages.remove)}
              </SMLButton>
            </div>
          )}
          <div className={clsx(styles.opt, styles.optPlain)}>
            <b>{intl.formatMessage(copy.keep, { count: request.fileCount ?? 0 })}</b>
            <small>{keepDetail}</small>
            {request.downloadHref !== undefined ? (
              <a
                className={clsx(styles.link, styles.optAct)}
                href={request.downloadHref}
                download
                onClick={onContinue}
                data-testid="watermark-choice-continue"
              >
                {keepAction}
              </a>
            ) : (
              <button
                type="button"
                className={clsx(styles.link, styles.optAct)}
                onClick={onContinue}
                data-testid="watermark-choice-continue"
              >
                {keepAction}
              </button>
            )}
          </div>
        </div>
        {cleanCopiesAvailable && files > 0 && !isMember && (
          <p className={styles.fine} data-testid="watermark-choice-copies">
            {intl.formatMessage(plan === 'trial' ? messages.fineTrial : messages.fineFree, { count: files })}
          </p>
        )}
      </div>
    </Modal>
  );
};

export const WatermarkChoiceModal = TypedMemo(WatermarkChoiceModalComponent);
